import React, { useState, useRef } from "react";
import {
  Container,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Typography,
  Box,
  Button,
  Modal,
  IconButton,
  Divider,
  CircularProgress,
  Fade,
  Zoom,
  Backdrop
} from "@mui/material";
import LinesDecor from "markup/components/LinesDecor";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import PageHeader from "../components/PageHeader";
import axiosClient from "axiosClient";
import logoImg from '../../assets/centrum_assets/design/index/LOGO.png'

// 1. React Query Import
import { useQuery } from '@tanstack/react-query';

// --- SHARED COMPONENTS ---
import DOMPurify from "dompurify";
import RichTextContent from "../components/RichTextContext";

// --- SWIPER IMPORTS ---
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

// Assets
import modalbg from '../../assets/centrum_assets/design/index/fourthBG.png';
import productsBackground from '../../assets/centrum_assets/design/stations/stationBG.png'

// --- HELPER: Image Fade ---
const FadeInImage = ({ sx = {}, ...props }) => {
  const [loaded, setLoaded] = useState(false);
  return (
    <CardMedia
      component="img"
      onLoad={() => setLoaded(true)}
      sx={{
        ...sx,
        opacity: loaded ? 1 : 0,
        transition: sx.transition 
          ? `${sx.transition}, opacity 0.8s ease-in-out` 
          : "opacity 0.8s ease-in-out",
        pointerEvents: "none", 
      }}
      {...props}
    />
  );
};

// --- HELPER: Strip HTML ---
const stripHtml = (html) => {
    if (!html) return "";
    return html.replace(/<[^>]*>?/gm, '');
};

const getFuelColor = (productTitle) => {
    if (!productTitle) return '#3d3939';
    const title = productTitle.toLowerCase();
    if (title.includes('93') || title.includes('eco')) return '#28a745';
    if (title.includes('96') || title.includes('premium')) return '#dc3545';
    if (title.includes('diesel')) return '#ffc107';
    return '#3d3939';
};

// ==========================================
// SUB-COMPONENTS (With Centering Logic)
// ==========================================

const FuelSection = ({ products, groupName }) => {
  // Logic: If the remaining items are 1 or 2, center the row.
  const isOddRemainder = products.length % 3 !== 0;

  return (
    <Box>
      <Box sx={{ display: "block", textAlign: { xs: "left", lg: "left" }, mb: 4, mt:{lg: 10, sm: 8} }}>
        <Typography variant="h1" fontWeight={750} sx={{ mb: 1, letterSpacing: 1, color: "#222843", fontSize: { xs: "3rem", md: "3rem" } }}>
          {groupName}
        </Typography>
        <Divider sx={{ borderBottomWidth: 6, borderColor: "#999", borderRadius: 3, width: { lg: "7%", md: '10%',sm: '32%', xs: "55%" }, mt: { lg: 2, xs: 1 }, mb: { xs: 2, lg: 1 } }} />
      </Box>

      <Grid 
        container 
        spacing={2} 
        sx={{ mb: 6 }}
        // Apply Center logic
        justifyContent={isOddRemainder ? "center" : "flex-start"}
      >
        {products.map((item, index) => (
          <Grid item xs={12} sm={4} key={index} sx={{ textAlign: { xs: "center", sm: "left" } }}>
            <FadeInImage src={item.img} sx={{ height: "100%", width: "100%", objectFit: "contain", display: "block", margin: { xs: "0 auto", sm: 0 }, cursor: "default" }} />
            <h4 style={{fontWeight: 850, color: getFuelColor(item.title) }}>
              {item.title}
            </h4>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

const GasSection = ({ products, groupName, onOpenModal }) => {
  // Logic: If the remaining items are 1 or 2, center the row.
  const isOddRemainder = products.length % 3 !== 0;

  return (
    <Box>
      <Box sx={{ display: "block", textAlign: { xs: "left", lg: "left" }, mb: 4 }}>
        <Typography variant="h2" fontWeight={750} sx={{ mb: 1, letterSpacing: 1, color: "#222843", fontSize: { xs: "3rem", md: "3rem" } }}>
          {groupName}
        </Typography>
        <Divider sx={{ borderBottomWidth: 6, borderColor: "#999", borderRadius: 3, width: { lg: "7%", md: '10%',sm: '32%', xs: "55%" }, mt: { lg: 4, xs: 1 }, mb: { xs: 2, lg: 1 } }} />
      </Box>

      <Grid 
        container 
        spacing={4} 
        sx={{ mb: 6 }}
        // Apply Center logic
        justifyContent={isOddRemainder ? "center" : "flex-start"}
      >
        {products.map((item, index) => (
          <Grid item xs={12} sm={6} md={4} key={index} display="flex" justifyContent="center">
            <Card onClick={() => onOpenModal(item)} sx={{ borderRadius: 2, width: "100%", maxWidth: 400, cursor: "pointer", display: "flex", flexDirection: "column", height: { xs: "auto", md: 480 } }}>
              <Box sx={{ overflow: "hidden", height: { xs: 250, md: 400 }, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "#fff" }}>
                <FadeInImage src={item.img} sx={{ height: "100%", width: "auto", objectFit: "contain", transition: "transform 0.3s ease", "&:hover": { transform: "scale(1.05)" } }} />
              </Box>
              <CardContent>
                <Typography sx={{ fontSize: "15px", fontWeight: 700, color: "#00b5e2" }}>{item.title}</Typography>
                <Typography sx={{ fontSize: "14px", fontWeight: 600, letterSpacing: 1.2, color: "text.secondary" }}>
                  {stripHtml(item.desc) || item.title}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

const StandardSection = ({ products, groupName, visibleCount, onShowMore, onShowLess, onOpenModal }) => {
  // Logic: Check alignment based on the VISIBLE cards, not total cards.
  const visibleProducts = products.slice(0, visibleCount);
  const isOddRemainder = visibleProducts.length % 3 !== 0;

  return (
    <Box>
      <Box sx={{ display: "block", textAlign: { xs: "left", lg: "left" }, mb: 4 }}>
        <Typography variant="h2" fontWeight={750} sx={{ mb: 1, letterSpacing: 1, color: "#222843", fontSize: { xs: "3rem", md: "3rem" } }}>
          {groupName}
        </Typography>
        <Divider sx={{ borderBottomWidth: 6, borderColor: "#999", borderRadius: 3, width: { lg: "7%", md: '10%',sm: '32%', xs: "75%" }, mt: { lg: 4, xs: 1 }, mb: { xs: 2, lg: 1 } }} />
      </Box>

      <Grid 
        container 
        spacing={3}
        // Apply Center logic dynamic based on show more/less
        justifyContent={isOddRemainder ? "center" : "flex-start"}
      >
        {visibleProducts.map((item, index) => (
          <Grid item xs={12} sm={4} key={index} onClick={() => onOpenModal(item)} sx={{ cursor: "pointer" }}>
            <Card sx={{ borderRadius: 2, transition: "box-shadow 0.3s ease", height: { lg: 520, md: 350, sm: 300, xs: "auto" }, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
              <Box sx={{ overflow: "hidden", height: { lg: 400, sm: 400, xs: 250 }, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
                <FadeInImage src={item.img} sx={{ width: "100%", height: "95%", objectFit: "contain", transform: "scale(1.15)", transition: "transform 0.3s ease", "&:hover": { transform: "scale(1.22)" } }} />
              </Box>
              <CardContent>
                <Typography sx={{ fontWeight: 700, fontSize: "15px", color: "#00b5e2" }}>{item.title}</Typography>
                <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "text.secondary", overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", height: "3em" }}>
                  {stripHtml(item.desc) || "Detailed info available in modal."}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {products.length > 6 && (
         <Box textAlign="center" mt={5} display="flex" justifyContent="center" gap={2} sx={{ position: 'relative', zIndex: 20 }}>
          {visibleCount < products.length && (
            <Button onClick={onShowMore} variant="contained" sx={{ backgroundColor: "#00bbd4", px: 3, borderRadius: "40px", letterSpacing: 2, fontSize: '1.4rem', fontWeight: 550, "&:hover": { backgroundColor: "#009fb6" } }}>
              Show More
            </Button>
          )}
          {visibleCount > 6 && (
            <Button onClick={onShowLess} variant="contained" sx={{ color: 'white', backgroundColor: "#00bbd4", px: 3, borderRadius: "40px", letterSpacing: 2, fontSize: '1.4rem', fontWeight: 550, "&:hover": { backgroundColor: "#009fb6" } }}>
              Show Less
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};

// ==========================================
// MAIN COMPONENT
// ==========================================

function Solutions() {
  const [visibleSections, setVisibleSections] = useState({}); 
  const [modalOpen, setModalOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  // References for Swipers
  const swiperRef = useRef(null);

  // React Query
  const { data: productGroups = [], isLoading: loading } = useQuery({
    queryKey: ['products'],
    queryFn: async () => {
      const response = await axiosClient.get('/products');
      return response.data.data || [];
    },
  });

  // Flat list for Modal Navigation
  const modalProducts = productGroups
    .filter(g => !g.category.toLowerCase().includes('fuel'))
    .flatMap(g => g.products);

  // Handlers
  const handleShowMore = (category) => {
    setVisibleSections(prev => ({ ...prev, [category]: (prev[category] || 6) + 3 }));
  };

  const handleShowLess = (category) => {
    setVisibleSections(prev => ({ ...prev, [category]: 6 }));
  };

  const openModal = (item) => {
    const index = modalProducts.findIndex(p => p.id === item.id);
    if (index !== -1) {
      setCurrentIndex(index);
      setModalOpen(true);
    }
  };

  const closeModal = () => setModalOpen(false);

  // Navigation for Swiper
  const showNext = (e) => {
    e?.stopPropagation();
    swiperRef.current?.slideNext();
  };

  const showPrev = (e) => {
    e?.stopPropagation();
    swiperRef.current?.slidePrev();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="70vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <PageHeader title="PRODUCTS" isProducts />

      <Box sx={{ backgroundImage: `url(${productsBackground})` }}>
        <Container maxWidth="lg" sx={{ py: 5 }}>
          {productGroups.map((group, groupIndex) => {
            const categoryName = group.category.toLowerCase();
            const visibleCount = visibleSections[group.category] || 6;

            if (categoryName.includes('fuel')) {
              return <FuelSection key={groupIndex} products={group.products || []} groupName={group.category.toUpperCase()} />;
            }
            if (categoryName.includes('gas')) {
              return <GasSection key={groupIndex} products={group.products || []} groupName={group.category.toUpperCase()} onOpenModal={openModal} />;
            }
            return (
              <StandardSection 
                key={groupIndex} 
                products={group.products || []} 
                groupName={group.category.toUpperCase()} 
                visibleCount={visibleCount}
                onShowMore={() => handleShowMore(group.category)}
                onShowLess={() => handleShowLess(group.category)}
                onOpenModal={openModal}
              />
            );
          })}
        </Container>
      </Box>

      {/* ========================================================= */}
      {/* MODAL SWIPER (Matching StationDetails Style) */}
      {/* ========================================================= */}
      <Modal 
        open={modalOpen} 
        onClose={closeModal}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{ backdrop: { timeout: 450 } }}
        sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1300,
        }}
      >
        <Fade in={modalOpen} timeout={450}>
            {/* Modal Wrapper */}
            <Box sx={{ 
                width: '100vw', 
                height: '100vh', 
                outline: 'none', 
                position: 'relative', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center'
            }}>
                {/* Close Button */}
                <IconButton                                                                                                                             
                    onClick={closeModal}
                    sx={{ 
                        position: "absolute", 
                        top: { xs: 15, md: 20 }, 
                        right: { xs: 15, md: 20 }, 
                        zIndex: 2200, 
                        color: "white", 
                        backgroundColor: "rgba(0,0,0,0.3)", 
                        "&:hover": { backgroundColor: "rgba(0,0,0,0.6)" } 
                    }}
                >
                    <CloseIcon sx={{ fontSize: { xs: 24, md: 30 } }} />
                </IconButton>

                {/* Arrows (Hidden on mobile, visible on desktop) */}
                <IconButton onClick={showPrev} sx={{ position: "absolute", left: { xs: 5, md: 20 }, top: "50%", transform: "translateY(-50%)", zIndex: 200, color: "white", backgroundColor: "rgba(0,0,0,0.3)", width: { xs: 40, md: 60 }, height: { xs: 40, md: 60 }, display: { xs: 'none', md: 'flex' }, "&:hover": { backgroundColor: "rgba(0,0,0,0.6)" } }}>
                    <ArrowBackIosNewIcon sx={{ fontSize: { xs: 20, md: 35 } }} />
                </IconButton>
                <IconButton onClick={showNext} sx={{ position: "absolute", right: { xs: 5, md: 20 }, top: "50%", transform: "translateY(-50%)", zIndex: 200, color: "white", backgroundColor: "rgba(0,0,0,0.3)", width: { xs: 40, md: 60 }, height: { xs: 40, md: 60 }, display: { xs: 'none', md: 'flex' }, "&:hover": { backgroundColor: "rgba(0,0,0,0.6)" } }}>
                    <ArrowForwardIosIcon sx={{ fontSize: { xs: 20, md: 35 } }} />
                </IconButton>

                <Zoom in={modalOpen} timeout={450}>
                    <Box sx={{ width: '100%', height: '100%' }}>
                        <Swiper
                            modules={[Navigation, Pagination]}
                            onBeforeInit={(swiper) => { swiperRef.current = swiper; }}
                            initialSlide={currentIndex}
                            loop={true}
                            speed={700}
                            spaceBetween={50}
                            slidesPerView={1}
                            grabCursor={true}
                            pagination={{ clickable: true }} 
                            style={{ 
                                width: '100%', 
                                height: '100%',
                                "--swiper-pagination-color": "#fff", 
                                "--swiper-pagination-bullet-inactive-color": "rgba(255,255,255, 0.5)",
                                "--swiper-pagination-bullet-size": "16px",
                                "--swiper-pagination-bullet-horizontal-gap": "6px"
                            }}
                        >
                            {modalProducts.map((product, index) => (
                                <SwiperSlide key={index}>
                                    <Box onClick={closeModal} sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                        <Box onClick={(e) => e.stopPropagation()} sx={{
                                            position: "relative",
                                            width: { xs: "80vw", md: "70vw", sm : '90vw', }, 
                                            height: "auto",
                                            maxHeight: { xs: "85vh", md: "85vh" }, 
                                            bgcolor: "background.paper",
                                            borderRadius: 2,
                                            boxShadow: 24,
                                            p: { xs: 2, md: 6 }, 
                                            display: "flex",
                                            flexDirection: { xs: "column", md: "row" }, 
                                            backgroundImage: `url(${modalbg})`,
                                            backgroundSize: 'cover',
                                            outline: "none",
                                            overflowY: 'auto', 
                                            gap: { xs: 2, md: 0 },
                                            userSelect: "none", 
                                            WebkitUserSelect: "none",
                                            touchAction: "pan-y", 
                                        }}>
                                            {/* Image Section */}
                                            <Box sx={{ flex: { md: "0 0 45%" }, width: "100%", display: "flex", justifyContent: "center", alignItems: "center", position: "relative" }}>
                                                <FadeInImage src={product.img} alt={product.title} sx={{ maxWidth: "100%", maxHeight: { xs: "40vh", md: "45vh" }, objectFit: "contain", borderRadius: 8, pointerEvents: "none" }} />
                                                <Box component="img" src={logoImg} alt="Logo" sx={{ display: { xs: "none", md: "block" }, position: "absolute", bottom: -30, left: -30, width: "200px", height: "auto", m: 0 }} />
                                            </Box>
                                            
                                            {/* Content Section */}
                                            <Box sx={{ flex: 1, display: "flex", flexDirection: "column", textAlign: { xs: "left", md: "left" } }}>
                                                <Typography variant="h1" fontWeight="800" mb={2} sx={{ color: "#0a1a3b", fontSize: { xs: "3rem", md: "3.5rem" }, lineHeight: 1.2 }}>
                                                    {product.title}
                                                </Typography>
                                                
                                                <RichTextContent
                                                  component="div"
                                                  sx={{
                                                    fontSize: { xs: "1.7rem", md: "2rem" },
                                                    lineHeight: 1.6,
                                                    textAlign: "justify",
                                                    color: "#4a4a4a",
                                                    '& p': { margin: 0, marginBottom: '1em', fontWeight: 400 },
                                                    '& strong': { fontWeight: 700 },
                                                    '& ul': { paddingLeft: '1.5em', marginBottom: '1em' },
                                                    '& ol': { paddingLeft: '1.5em', marginBottom: '1em' },
                                                  }}
                                                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(product.desc || "No description available.") }}
                                                />
                                                
                                                <Box component="img" src={logoImg} alt="Logo" sx={{ display: { xs: "block", md: "none" }, width: "100px", height: "auto", mt: 1 }} />
                                            </Box>
                                        </Box>
                                    </Box>
                                </SwiperSlide>
                            ))}
                        </Swiper>
                    </Box>
                </Zoom>
            </Box>
        </Fade>
      </Modal>

      <Box sx={{ position: "Relative", width: "100%",}}>
        <LinesDecor greenMt={{ xl: -5, lg: -5, md: -4, sm: -2, xs: 1 }} blueMt={{ xl: 0, lg: 1, md: 2, sm: 1, xs: 3 }}/> 
      </Box>
    </>
  );
}

export default Solutions;